import { useEffect, useMemo, useRef } from 'react';

import { BarCustomLayerProps, BarDatum } from '@nivo/bar';
import { Margin } from '@nivo/core';
import * as echarts from 'echarts';

import { CommonChartProps } from './chartsCommons'; // adjust the path
import { chartColors } from '../../configs/chart.config';

export type BarChartProps<T> = CommonChartProps<T> & {
  yAxisLabel: string;
  layer?: (props: BarCustomLayerProps<BarDatum>) => JSX.Element;
  chartMargin?: Partial<Margin>;
  hideTooltip?: boolean;
  width?: number | string;
  height?: number | string;
  className?: string;
};

export const EChartsBarChartHorizontalDots = <T,>({
  data,
  keyField,
  valueField,
  yAxisLabel,
  formatKey,
  valueFormatOptions,
  width = '100%',
  height = '480px',
  className = '',
  chartMargin,
  hideTooltip = false,
  mode = 'card',
}: BarChartProps<T>) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const chartOption = useMemo(() => {
    if (!data || data.length === 0) return null;

    const getKey = (item: T) =>
      typeof keyField === 'function' ? keyField(item) : (item as any)[keyField];

    const getValue = (item: T) => {
      if (!valueField) return 0;

      if (typeof valueField === 'function') {
        return valueField(item);
      }

      const key = valueField as keyof T;
      return Number((item as Record<keyof T, unknown>)[key]);
    };

    const labels = data.map((item) => {
      const rawKey = getKey(item);
      return formatKey ? formatKey(String(rawKey)) : String(rawKey);
    });

    const values = data.map((item) => {
      const rawVal = getValue(item);
      if (rawVal == null) return 0;

      return rawVal;
    });
    const numericHeightValue = parseInt(height, 10);
    const isCompactView = numericHeightValue <= 480;

    const maxDots = mode === 'card' ? numericHeightValue / 12 : 70;
    const maxValue = Math.max(...values);
    const scale = maxValue / maxDots || 1;

    const scatterSeries = data.map((item, index) => {
      const rawKey = getKey(item);
      const label = formatKey ? formatKey(String(rawKey)) : String(rawKey);
      const value = values[index];
      const dots = Math.max(1, Math.floor(value / scale));

      const fillColor = chartColors.colorPalette[index % chartColors.colorPalette.length];
      const borderColor = chartColors.colorPalette[(index + 1) % chartColors.colorPalette.length];

      return {
        name: label,
        type: 'scatter',
        symbol: 'circle',
        symbolSize: mode === 'card' ? numericHeightValue / 35 : 14,
        data: Array.from({ length: dots }, (_, i) => [
          ((i + 0.5) * value) / dots, // x-value (dot value)
          label, // y-axis category label
        ]),
        itemStyle: {
          color: fillColor,
          borderColor: borderColor,
          borderWidth: 2,
        },
      };
    });

    return {
      backgroundColor: '#1F1F1F',
      grid: {
        top: 35,
        left: 50,
        right: 20,
        bottom: 20,
        containLabel: true,
        // ...(chartMargin || {}),
      },
      tooltip: hideTooltip
        ? undefined
        : {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
            },
            backgroundColor: 'rgba(66,66,66,0.95)',
            borderWidth: 0,
            borderRadius: 4,
            padding: 12,
            textStyle: {
              color: '#fff',
              fontWeight: 'bold',
            },
            formatter: (params: any) => {
              if (!params.length) return '';
              const item = params[0];
              const index = labels.indexOf(item.name);
              const totalValue = values[index];
              return `
                <div style="padding: 4px 8px; max-width: 300px;">
      <p style="margin: 0; font-weight: bold; font-size: 12px; color: white;">
        ${item.name}
      </p>
      <p style="margin: 0; font-size: 12px; color: ${item.color};">
        ${item.seriesName}: ${new Intl.NumberFormat(undefined, valueFormatOptions).format(totalValue)}
      </p>
    </div>
              `;
            },
          },
      xAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#9E9E9E',
          fontSize: 12,
          padding: [15, 0, 0, 0],
        },
        axisLabel: {
          color: '#9E9E9E',
          ...(isCompactView && {
            formatter: (val: string) => {
              const num = Number(val);
              if (isNaN(num)) return val;
              return new Intl.NumberFormat('en-US', {
                notation: 'compact',
                compactDisplay: 'short',
                maximumFractionDigits: 2,
              }).format(num);
            },
          }),
        },
        splitLine: {
          lineStyle: { color: '#28303E' },
        },
      },
      yAxis: {
        type: 'category',
        data: labels,
        axisLine: {
          lineStyle: { color: '#28303E' },
        },
        axisLabel: {
          margin: 10,
          color: '#9E9E9E',
          formatter: (val: string) => val,
        },
      },
      series: scatterSeries,
    };
  }, [
    data,
    keyField,
    valueField,
    yAxisLabel,
    formatKey,
    valueFormatOptions,
    // chartMargin,
    hideTooltip,
  ]);

  useEffect(() => {
    if (chartRef.current && chartOption) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }
      chartInstance.current.setOption(chartOption);
    }
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [chartOption]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-slate-500" style={{ width, height }}>
        No data available
      </div>
    );
  }

  return (
    <div className={`relative mx-auto overflow-hidden${className}`}>
      <div
        ref={chartRef}
        style={{
          width,
          height,
          position: 'relative',
        }}
      />
    </div>
  );
};
